import React from 'react';
import * as Sentry from '@sentry/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { RefreshCw, Home } from 'lucide-react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Convert React's ErrorInfo to a plain object that Sentry can handle
    const sentryErrorInfo = {
      componentStack: errorInfo.componentStack,
    };

    Sentry.withScope((scope) => {
      scope.setExtras(sentryErrorInfo);
      Sentry.captureException(error);
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <ErrorView
          error={this.state.error}
          reset={() => this.setState({ hasError: false, error: null })}
        />
      );
    }

    return this.props.children;
  }
}

interface ErrorViewProps {
  error: Error | null;
  reset: () => void;
}

function ErrorView({ error, reset }: ErrorViewProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-4">
          <h1 className="text-4xl font-bold text-destructive">Oops!</h1>
          <p className="text-xl text-muted-foreground">Something went wrong</p>
          {error && (
            <pre className="mt-4 p-4 bg-muted rounded-lg text-sm text-left overflow-auto max-h-48">
              {error.message}
            </pre>
          )}
        </div>
        <div className="flex gap-4 justify-center">
          <Button onClick={reset} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button onClick={() => navigate('/dashboard')} variant="default">
            <Home className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          If this problem persists, please contact support.
        </p>
      </div>
    </div>
  );
}

export default ErrorBoundary;
